import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/App';
import usePeriodChange from '@/hooks/usePeriodChange';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Building2, Camera, Copy, EyeOff, Eye, Package, Pencil, Plus, Trash2, RefreshCw } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { canManageFinance } from '@/lib/permissions';
import html2canvas from 'html2canvas';
import RekapanSemuaPartai from '@/pages/RekapanSemuaPartai';
import { pillTabsList, pillTabsTrigger } from '@/lib/tabStyles';

const RekapanSemuaCabang = ({ onBranchSelect, user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stokGudangByBranch, setStokGudangByBranch] = useState({});
  const [stockRecords, setStockRecords] = useState([]);
  const [stokDialogOpen, setStokDialogOpen] = useState(false);
  const [editingStokId, setEditingStokId] = useState(null);
  const [stokForm, setStokForm] = useState({ date: '', pembelian_baru: '', bahan_dikirim: '', bahan_terjual: '' });
  const [savingStok, setSavingStok] = useState(false);
  const [rekapData, setRekapData] = useState([]);
  const [centralExpensesTotal, setCentralExpensesTotal] = useState(0);
  const [defaultOperationalCost, setDefaultOperationalCost] = useState(100);
  const [periodName, setPeriodName] = useState('');
  const [sebagianSummary, setSebagianSummary] = useState({ modal: 0, beratPenjualan: 0, biayaPusat: 0, rpJual: 0 });
  const [activePeriodId, setActivePeriodId] = useState('');
  const [recalcingOp, setRecalcingOp] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [hideZeroBranches, setHideZeroBranches] = useState(false);
  const [hideNoSalesBranches, setHideNoSalesBranches] = useState(false);
  const [allExpenses, setAllExpenses] = useState([]);
  const [allCentralExpenses, setAllCentralExpenses] = useState([]);
  const [branchesList, setBranchesList] = useState([]);
  const [penjualanRekap, setPenjualanRekap] = useState([]);
  const cardRef = useRef(null);
  const sebagianRef = useRef(null);
  const biayaRef = useRef(null);
  const penjualanRef = useRef(null);

  useEffect(() => {
    fetchRekapData();
  }, []);

  usePeriodChange(() => fetchRekapData(), { includeSettings: true });

  const fetchRekapData = async () => {
    setLoading(true);
    try {
      // Get selected period from localStorage
      const periodId = localStorage.getItem('selectedPeriodId');
      
      // Build query params
      const params = periodId ? { period_id: periodId } : {};

      // Fetch all data including system settings, central expenses, and periods
      const [branchesRes, purchasesRes, salesRes, expensesRes, settingsRes, centralExpensesRes, periodsRes, stockRes] = await Promise.all([
        api.get('/branches'),
        api.get('/purchases', { params }),
        api.get('/sales', { params }),
        api.get('/expenses', { params }),
        api.get('/settings'),
        api.get('/central-expenses', { params }),
        api.get('/periods'),
        api.get('/stock/records', { params })
      ]);
      
      // Get period name + period-level default operational cost
      const currentPeriod = (periodId && periodsRes.data) ? periodsRes.data.find(p => p.id === periodId) : null;
      setPeriodName(currentPeriod ? currentPeriod.name : '');
      const activeId = periodId || (periodsRes.data || []).find(p => !p.is_closed)?.id || '';
      setActivePeriodId(activeId);

      const allBranches = branchesRes.data;
      const allPurchases = purchasesRes.data;
      const allSales = salesRes.data;
      const allExpenses = expensesRes.data;
      const allCentralExpenses = centralExpensesRes.data;
      
      // Calculate total central expenses
      const totalCentralExpenses = allCentralExpenses.reduce((sum, exp) => sum + (exp.amount || 0), 0);
      setCentralExpensesTotal(totalCentralExpenses);
      
      // Ringkasan untuk tabel bawah "Rekapan Sebagian": Modal / Berat Penjualan /
      // Biaya Pusat memakai angka yang sama dengan History Penjualan di Dashboard,
      // Rp Jual = total rupiah penjualan tanpa Transfer Stok.
      const realSales = allSales.filter(s => s.keterangan !== 'Stok');
      const rpJual = realSales.reduce((sum, s) => sum + (s.total_amount || 0), 0);
      const beratPenjualan = realSales.reduce((sum, s) => sum + (s.quantity_kg || 0), 0);
      let modalValue = 0;
      let biayaPusatValue = 0;
      const periodYear = (currentPeriod?.months || [])[0]?.slice(0, 4);
      if (periodId && periodYear) {
        try {
          const ys = await api.get('/periods/yearly-summary', { params: { year: Number(periodYear) } });
          const periodRows = (ys.data || []).filter(r => r.period_id === periodId);
          if (periodRows.length > 0) {
            modalValue = periodRows[0].modal || 0;
            const months = periodRows[0].dividend_months || periodRows.length || 1;
            biayaPusatValue = (periodRows[0].biaya_pusat || 0) * months;
          }
        } catch (summaryError) {
          console.error('Failed to fetch yearly summary for rekapan sebagian:', summaryError);
        }
      }
      setSebagianSummary({ modal: modalValue, beratPenjualan, biayaPusat: biayaPusatValue, rpJual });
      
      // Default operational cost now comes from the selected PERIOD (fallback to global setting)
      const defaultCost = (currentPeriod && currentPeriod.default_operational_cost)
        ? currentPeriod.default_operational_cost
        : (settingsRes.data?.default_operational_cost || 0);
      const overrides = settingsRes.data?.branch_operational_overrides || [];
      const overrideMap = {};
      overrides.forEach((o) => { overrideMap[o.branch_id] = o.cost_per_gr; });
      setDefaultOperationalCost(defaultCost);

      // Calculate data for each branch (total across all partais)
      const calculatedData = allBranches.map((branch, index) => {
        // Use branch override cost if present, else default
        const currentOperationalCost = overrideMap[branch.id] !== undefined ? overrideMap[branch.id] : defaultCost;
        // Filter data for this branch
        const branchPurchases = allPurchases.filter(p => p.branch_id === branch.id);
        const branchSales = allSales.filter(s => s.branch_id === branch.id);
        const branchExpenses = allExpenses.filter(e => e.branch_id === branch.id);

        // Calculate stok awal from purchases with keterangan "Stok Awal"
        const stokAwalGr = branchPurchases.reduce((sum, p) => {
          if (p.items) {
            const stokAwalWeight = p.items
              .filter(item => item.keterangan === 'Stok Awal')
              .reduce((itemSum, item) => itemSum + (item.weight_kg || 0), 0);
            return sum + stokAwalWeight;
          }
          return sum;
        }, 0);

        // Calculate total weights across all partais (already in grams from user input)
        const totalBeratBeli = branchPurchases.reduce((sum, p) => {
          const purchaseWeight = p.items ? p.items.reduce((itemSum, item) => itemSum + (item.weight_kg || 0), 0) : 0;
          return sum + purchaseWeight;
        }, 0); // Already in grams, no conversion needed

        // Berat Beli (gr) = Total Berat Beli - Stok Awal
        const beratBeli = totalBeratBeli - stokAwalGr;

        // Calculate berat jual (exclude items with keterangan "Stok")
        const beratJual = branchSales.reduce((sum, s) => {
          if (s.keterangan !== 'Stok') {
            return sum + (s.quantity_kg || 0);
          }
          return sum;
        }, 0); // Already in grams

        // Calculate stok akhir from sales with keterangan "Stok"
        const stokAkhirGr = branchSales.reduce((sum, s) => {
          if (s.keterangan === 'Stok') {
            return sum + (s.quantity_kg || 0);
          }
          return sum;
        }, 0); // Already in grams

        // Calculate +/- gr (using actual berat beli and berat jual after stok adjustments)
        const plusMinusGr = (beratJual + stokAkhirGr) - (beratBeli + stokAwalGr);
        const plusMinusPersen = (beratBeli + stokAwalGr) > 0 ? (plusMinusGr / (beratBeli + stokAwalGr)) * 100 : 0;

        // Calculate Rp Stok Awal from purchases with keterangan "Stok Awal"
        const rpStokAwal = branchPurchases.reduce((sum, p) => {
          if (p.items) {
            // Calculate total amount for Stok Awal items only
            const stokAwalAmount = p.items
              .filter(item => item.keterangan === 'Stok Awal')
              .reduce((itemSum, item) => itemSum + (item.subtotal || 0), 0);
            return sum + stokAwalAmount;
          }
          return sum;
        }, 0);

        // Calculate total rupiah values
        const totalRpBeli = branchPurchases.reduce((sum, p) => sum + (p.total_amount || 0), 0);
        
        // Rp Beli = Total Rp Beli - Rp Stok Awal
        const rpBeli = totalRpBeli - rpStokAwal;
        
        // Calculate rpJual (exclude sales with keterangan "Stok")
        const rpJual = branchSales.reduce((sum, s) => {
          if (s.keterangan !== 'Stok') {
            return sum + (s.total_amount || 0);
          }
          return sum;
        }, 0);
        
        // Calculate rpStokAkhir from sales with keterangan "Stok"
        const rpStokAkhir = branchSales.reduce((sum, s) => {
          if (s.keterangan === 'Stok') {
            return sum + (s.total_amount || 0);
          }
          return sum;
        }, 0);

        // Calculate costs - separate biayaOP from biayaPusat
        // Biaya OP: Manual expenses only (exclude auto-generated operational cost expenses)
        const biayaOP = branchExpenses.reduce((sum, e) => {
          // Exclude auto-generated expenses (contains "Biaya Operasional - Partai")
          if (e.description && e.description.includes('Biaya Operasional - Partai')) {
            return sum;
          }
          return sum + (e.amount || 0);
        }, 0);
        // Biaya Pusat: Auto-calculated operational cost (Rp/gr × berat jual)
        const biayaPusat = beratJual * currentOperationalCost;
        // Biaya OP/gr = (Biaya OP + Biaya Pusat) / Berat Jual (merged)
        const biayaOPPerGr = beratJual > 0 ? (biayaOP + biayaPusat) / beratJual : 0;

        // Calculate profit/loss using adjusted values
        const labaRugi = rpJual + rpStokAkhir - rpStokAwal - rpBeli - biayaOP - biayaPusat;
        // LabaRugi/gr = LabaRugi / Berat Jual (gr)
        const labaRugiPerGr = beratJual > 0 ? labaRugi / beratJual : 0;

        return {
          branch_id: branch.id,
          branch_name: branch.name,
          branch_status: branch.status || 'ACTIVE',
          hasTransactions: branchPurchases.length > 0 || branchSales.length > 0 || branchExpenses.length > 0,
          branch_order_number: branch.order_number || 9999,
          group_name: branch.group_name, // Include group_name for grouping
          order_number: branch.order_number || 9999, // For sorting
          database_index: index, // Preserve original database order
          stokAwalGr,
          beratBeli,
          beratJual,
          stokAkhirGr,
          stok: stokAkhirGr,
          plusMinusGr,
          plusMinusPersen,
          rpStokAwal,
          rpBeli,
          rpJual,
          rpStokAkhir,
          rpStok: rpStokAkhir,
          biayaOP,
          biayaOPPerGr,
          biayaPusat,
          labaRugi,
          labaRugiPerGr
        };
      });

      // Keep original database order (don't sort)
      // User wants branches in the order they appear in database: Boby, Mira, Joe
      setRekapData(calculatedData);
      setAllExpenses(allExpenses);
      setAllCentralExpenses(allCentralExpenses);
      setBranchesList(allBranches);

      // ---- STOK ----
      // Stok Gudang: total berat pembelian pada partai yang sudah punya partai
      // tetapi belum memiliki penjualan sama sekali pada partai tersebut.
      const partaiWithSales = new Set();
      allSales.forEach((s) => {
        if (s.partai) partaiWithSales.add(`${s.branch_id}|${s.partai}`);
      });
      const gudang = {};
      allPurchases.forEach((p) => {
        if (!p.partai) return;
        if (partaiWithSales.has(`${p.branch_id}|${p.partai}`)) return;
        const w = p.items ? p.items.reduce((sum, item) => sum + (item.weight_kg || 0), 0) : 0;
        gudang[p.branch_id] = (gudang[p.branch_id] || 0) + w;
      });
      setStokGudangByBranch(gudang);

      // ---- REKAPAN PENJUALAN ----
      // Laba hanya dihitung dari partai yang punya penjualan. Partai pembelian yang
      // belum punya penjualan (masih stok gudang) tidak dihitung.
      const statusOrder = { ACTIVE: 1, INACTIVE: 2, BLACKLIST: 3 };
      const penjualanRows = [];
      allBranches.forEach((branch) => {
        const branchSales = allSales.filter(s => s.branch_id === branch.id);
        const partaisWithSales = Array.from(new Set(
          branchSales.filter(s => s.keterangan !== 'Stok').map(s => s.partai || '')
        ));

        const cost = overrideMap[branch.id] !== undefined ? overrideMap[branch.id] : defaultCost;
        let berat = 0;
        let laba = 0;

        partaisWithSales.forEach((partai) => {
          const pPurchases = allPurchases.filter(p => p.branch_id === branch.id && (p.partai || '') === partai);
          const pSales = branchSales.filter(s => (s.partai || '') === partai);
          const pExpenses = allExpenses.filter(e => e.branch_id === branch.id && (e.partai || '') === partai);

          const sumItems = (list, field, keterangan) => list.reduce((sum, p) => sum + (p.items || [])
            .filter(item => keterangan === undefined || item.keterangan === keterangan)
            .reduce((s, item) => s + (item[field] || 0), 0), 0);
          const sumSales = (field, isStok) => pSales.reduce((sum, s) =>
            ((s.keterangan === 'Stok') === isStok ? sum + (s[field] || 0) : sum), 0);

          const rpStokAwal = sumItems(pPurchases, 'subtotal', 'Stok Awal');
          const rpBeli = pPurchases.reduce((sum, p) => sum + (p.total_amount || 0), 0) - rpStokAwal;
          const beratJual = sumSales('quantity_kg', false);
          const rpJual = sumSales('total_amount', false);
          const rpStokAkhir = sumSales('total_amount', true);
          const biayaOP = pExpenses.reduce((sum, e) => (
            e.description && e.description.includes('Biaya Operasional - Partai') ? sum : sum + (e.amount || 0)
          ), 0);

          berat += beratJual;
          laba += rpJual + rpStokAkhir - rpStokAwal - rpBeli - biayaOP - (beratJual * cost);
        });

        penjualanRows.push({
          branch_id: branch.id,
          branch_name: branch.name,
          order_number: branch.order_number || 9999,
          status: branch.status || 'ACTIVE',
          status_rank: statusOrder[branch.status || 'ACTIVE'] || 4,
          hasSales: partaisWithSales.length > 0,
          berat,
          laba,
          margin: berat !== 0 ? laba / berat : 0,
        });
      });
      penjualanRows.sort((a, b) => (
        a.status_rank - b.status_rank
        || a.order_number - b.order_number
        || a.branch_name.localeCompare(b.branch_name)
      ));
      setPenjualanRekap(penjualanRows);

      setStockRecords(stockRes.data || []);
    } catch (error) {
      console.error('Failed to fetch rekap data:', error);
      toast.error('Gagal memuat data rekapan');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    return Math.round(num).toLocaleString('id-ID');
  };

  const formatCurrency = (num) => {
    return `Rp.\u00a0${Math.round(num).toLocaleString('id-ID')}`;
  };

  const getPercentageColor = (percent) => {
    if (percent < 0) return 'bg-red-100 text-red-800';
    if (percent >= 5) return 'bg-green-100 text-green-800';
    return '';
  };

  const getLabaRugiColor = (value) => {
    if (value < 0) return 'bg-red-100 text-red-800';
    if (value > 499) return 'bg-green-100 text-green-800';
    return '';
  };

  // Check if a branch has all zero values
  const isAllZero = (branch) => {
    return (
      branch.stokAwalGr === 0 &&
      branch.beratBeli === 0 &&
      branch.beratJual === 0 &&
      branch.stokAkhirGr === 0 &&
      branch.plusMinusGr === 0 &&
      branch.rpStokAwal === 0 &&
      branch.rpBeli === 0 &&
      branch.rpJual === 0 &&
      branch.rpStokAkhir === 0 &&
      branch.biayaOP === 0 &&
      branch.biayaPusat === 0 &&
      branch.labaRugi === 0
    );
  };

  // Filter branches based on hideZeroBranches toggle
  const getVisibleBranches = (branches) => {
    if (!hideZeroBranches) return branches;
    return branches.filter(branch => !isAllZero(branch));
  };

  // Count how many branches have all zero values
  const zeroBranchesCount = rekapData.filter(isAllZero).length;

  const handleBranchClick = (branchId) => {
    if (onBranchSelect) {
      onBranchSelect(branchId);
    }
  };

  const handlePusatClick = () => {
    // Navigate to central expenses page (Biaya Pusat)
    navigate('/transactions', { state: { selectedBranch: 'PUSAT' } });
  };

  const handleSaveToClipboard = async () => {
    await captureRef(cardRef, `Rekapan-Semua-Cabang${periodName ? '-' + periodName : ''}`);
  };

  const captureRef = async (targetRef, filename) => {
    if (!targetRef.current) return;
    
    setCapturing(true);
    
    // Wait for React to re-render and hide the button before capturing
    await new Promise(resolve => setTimeout(resolve, 100));
    
    const el = targetRef.current;
    el.setAttribute('data-capture-target', 'active');
    // Temporarily expand any horizontally-scrollable containers so the FULL
    // table width is captured even when the browser is narrower than the table.
    const scrollers = [el, ...el.querySelectorAll('*')].filter((node) => {
      const s = window.getComputedStyle(node);
      return /(auto|scroll)/.test(s.overflowX) || /(auto|scroll)/.test(s.overflow);
    });
    const savedStyles = scrollers.map((node) => ({ node, cssText: node.style.cssText }));
    scrollers.forEach((node) => {
      node.style.overflow = 'visible';
      node.style.overflowX = 'visible';
      node.style.maxWidth = 'none';
      node.style.width = `${node.scrollWidth}px`;
    });
    // Force reflow, then measure the full content width
    void el.offsetWidth;
    const fullWidth = Math.max(el.scrollWidth, el.offsetWidth);
    
    const restoreStyles = () => {
      savedStyles.forEach(({ node, cssText }) => { node.style.cssText = cssText; });
      el.removeAttribute('data-capture-target');
    };
    
    try {
      // html-to-image renders the real DOM (SVG foreignObject) → faithful to the on-screen layout
      const { toBlob } = await import('html-to-image');

      // Measure full width AFTER containers were expanded above
      const blob = await toBlob(el, {
        pixelRatio: 2,
        backgroundColor: '#ffffff',
        cacheBust: true,
        width: fullWidth,
        height: el.scrollHeight,
        style: { transform: 'none' },
      });

      // Restore original layout as soon as the DOM has been serialized
      restoreStyles();

      if (!blob) {
        toast.error('Gagal membuat gambar');
        setCapturing(false);
        return;
      }

      // Check if Clipboard API is supported
      if (navigator.clipboard && window.ClipboardItem) {
        try {
          await navigator.clipboard.write([
            new window.ClipboardItem({ 'image/png': blob })
          ]);
          toast.success('Gambar berhasil disalin ke clipboard!');
          setCapturing(false);
          return;
        } catch (err) {
          console.error('Error copying to clipboard:', err);
          // Fallback to download if clipboard fails
        }
      }

      // Fallback: Download the image (for Safari and unsupported browsers)
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('Gambar berhasil diunduh!');
      setCapturing(false);
    } catch (error) {
      restoreStyles();
      console.error('Error capturing screenshot:', error);
      toast.error('Gagal menangkap screenshot');
      setCapturing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  if (rekapData.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Building2 size={64} className="mx-auto mb-4 opacity-20" />
          <p className="text-lg text-slate-500">Belum ada data rekapan untuk periode ini</p>
        </CardContent>
      </Card>
    );
  }

  // Split branches: cabang non aktif/blacklist yang MASIH punya pembelian, biaya, atau
  // penjualan di periode terpilih tetap masuk tabel rekapan utama. Tabel "Non Aktif &
  // Blacklist" di bawah hanya memuat cabang non aktif yang benar-benar tanpa transaksi.
  const isInactiveOrBlacklist = (b) => b.branch_status === 'INACTIVE' || b.branch_status === 'BLACKLIST';
  const fullRekapData = rekapData;
  const inactiveRekapData = fullRekapData.filter((b) => isInactiveOrBlacklist(b) && !b.hasTransactions);
  const mainRekapData = fullRekapData.filter((b) => !isInactiveOrBlacklist(b) || b.hasTransactions);
  const nonAktifRekapData = inactiveRekapData.filter((b) => b.branch_status === 'INACTIVE');
  const blacklistRekapData = inactiveRekapData.filter((b) => b.branch_status === 'BLACKLIST');

  const renderStatusRekapTable = ({ data, title, subtitle, isBlacklist, testId, rowPrefix }) => (
    <Card className={isBlacklist ? 'border-red-200' : 'border-slate-300'} data-testid={testId}>
      <CardHeader>
        <CardTitle className={`flex items-center gap-2 text-lg ${isBlacklist ? 'text-red-700' : 'text-slate-700'}`}>
          <Building2 size={20} className={isBlacklist ? 'text-red-500' : 'text-slate-500'} />
          {title}
        </CardTitle>
        <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className={isBlacklist ? 'bg-red-50' : 'bg-slate-100'}>
                <TableHead className="text-xs whitespace-nowrap">No.</TableHead>
                <TableHead className="text-xs whitespace-nowrap">Nama Cabang</TableHead>
                <TableHead className="text-xs text-right whitespace-nowrap">Stok Awal (gr)</TableHead>
                <TableHead className="text-xs text-right whitespace-nowrap">Berat Beli (gr)</TableHead>
                <TableHead className="text-xs text-right whitespace-nowrap">Berat Jual (gr)</TableHead>
                <TableHead className="text-xs text-right whitespace-nowrap">Stok Akhir (gr)</TableHead>
                <TableHead className="text-xs text-right whitespace-nowrap">+/- gr</TableHead>
                <TableHead className="text-xs text-right whitespace-nowrap">+/- gr (%)</TableHead>
                <TableHead className="text-xs text-right whitespace-nowrap">Rp Beli</TableHead>
                <TableHead className="text-xs text-right whitespace-nowrap">Rp Jual</TableHead>
                <TableHead className="text-xs text-right whitespace-nowrap">Biaya OP + {defaultOperationalCost}</TableHead>
                <TableHead className="text-xs text-right whitespace-nowrap">LabaRugi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data
                .slice()
                .sort((a, b) => a.database_index - b.database_index)
                .map((branch, index) => (
                  <TableRow
                    key={branch.branch_id}
                    className={`${index % 2 === 0 ? 'bg-slate-50' : 'bg-white'} cursor-pointer hover:bg-slate-100`}
                    onClick={() => handleBranchClick(branch.branch_id)}
                    data-testid={`${rowPrefix}-${branch.branch_id}`}
                  >
                    <TableCell className="text-center text-slate-600">{index + 1}</TableCell>
                    <TableCell className="font-medium whitespace-nowrap text-slate-700">{branch.branch_name}</TableCell>
                    <TableCell className="text-right text-slate-700">{formatNumber(branch.stokAwalGr)}</TableCell>
                    <TableCell className="text-right text-slate-700">{formatNumber(branch.beratBeli)}</TableCell>
                    <TableCell className="text-right text-slate-700">{formatNumber(branch.beratJual)}</TableCell>
                    <TableCell className="text-right text-slate-700">{formatNumber(branch.stokAkhirGr)}</TableCell>
                    <TableCell className="text-right text-slate-700">{formatNumber(branch.plusMinusGr)}</TableCell>
                    <TableCell className="text-right text-slate-700">{branch.plusMinusPersen.toFixed(2)}%</TableCell>
                    <TableCell className="text-right text-slate-700">{formatCurrency(branch.rpBeli)}</TableCell>
                    <TableCell className="text-right text-slate-700">{formatCurrency(branch.rpJual)}</TableCell>
                    <TableCell className="text-right text-slate-700">{formatCurrency(branch.biayaOP + branch.biayaPusat)}</TableCell>
                    <TableCell className="text-right text-slate-700 font-semibold">{formatCurrency(branch.labaRugi)}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );

  // Check if any branch has a group
  const hasAnyGroup = mainRekapData.some(branch => branch.group_name && branch.group_name.trim() !== '');

  // Group branches by group_name and sort by order_number
  const groupedBranches = mainRekapData.reduce((groups, branch) => {
    const groupName = branch.group_name || 'Grup Pusat';
    if (!groups[groupName]) {
      groups[groupName] = [];
    }
    groups[groupName].push(branch);
    return groups;
  }, {});

  // Ensure the PUSAT row still shows when every branch already belongs to a real group
  if (hasAnyGroup && mainRekapData.length > 0 && !groupedBranches['Grup Pusat']) {
    groupedBranches['Grup Pusat'] = [];
  }

  // Sort branches within each group by database_index (preserve database order)
  Object.keys(groupedBranches).forEach(groupName => {
    groupedBranches[groupName].sort((a, b) => a.database_index - b.database_index);
  });

  // Sort group names (Grup Pusat at the TOP/first)
  const sortedGroupNames = Object.keys(groupedBranches).sort((a, b) => {
    if (a === 'Grup Pusat') return -1; // Grup Pusat first
    if (b === 'Grup Pusat') return 1;
    return a.localeCompare(b);
  });

  // Calculate totals per group
  const pusatLabaRugi = -centralExpensesTotal;
  const groupTotals = {};
  sortedGroupNames.forEach(groupName => {
    groupTotals[groupName] = groupedBranches[groupName].reduce((acc, branch) => ({
      stokAwalGr: acc.stokAwalGr + branch.stokAwalGr,
      beratBeli: acc.beratBeli + branch.beratBeli,
      beratJual: acc.beratJual + branch.beratJual,
      stokAkhirGr: acc.stokAkhirGr + branch.stokAkhirGr,
      plusMinusGr: acc.plusMinusGr + branch.plusMinusGr,
      rpStokAwal: acc.rpStokAwal + branch.rpStokAwal,
      rpBeli: acc.rpBeli + branch.rpBeli,
      rpJual: acc.rpJual + branch.rpJual,
      rpStokAkhir: acc.rpStokAkhir + branch.rpStokAkhir,
      biayaOP: acc.biayaOP + branch.biayaOP,
      biayaPusat: acc.biayaPusat + branch.biayaPusat,
      labaRugi: acc.labaRugi + branch.labaRugi
    }), {
      stokAwalGr: 0, beratBeli: 0, beratJual: 0, stokAkhirGr: 0, plusMinusGr: 0,
      rpStokAwal: 0, rpBeli: 0, rpJual: 0, rpStokAkhir: 0, biayaOP: 0, biayaPusat: 0, labaRugi: 0
    });
    
    // If this is Grup Pusat, add central expenses
    if (groupName === 'Grup Pusat') {
      groupTotals[groupName].biayaOP += centralExpensesTotal;
      groupTotals[groupName].labaRugi += pusatLabaRugi;
    }
  });

  // Calculate grand totals (main data only - inactive/blacklist w/o transactions excluded)
  const grandTotals = mainRekapData.reduce((acc, branch) => ({
    stokAwalGr: acc.stokAwalGr + branch.stokAwalGr,
    beratBeli: acc.beratBeli + branch.beratBeli,
    beratJual: acc.beratJual + branch.beratJual,
    stokAkhirGr: acc.stokAkhirGr + branch.stokAkhirGr,
    plusMinusGr: acc.plusMinusGr + branch.plusMinusGr,
    rpStokAwal: acc.rpStokAwal + branch.rpStokAwal,
    rpBeli: acc.rpBeli + branch.rpBeli,
    rpJual: acc.rpJual + branch.rpJual,
    rpStokAkhir: acc.rpStokAkhir + branch.rpStokAkhir,
    biayaOP: acc.biayaOP + branch.biayaOP,
    biayaPusat: acc.biayaPusat + branch.biayaPusat,
    labaRugi: acc.labaRugi + branch.labaRugi
  }), {
    stokAwalGr: 0,
    beratBeli: 0,
    beratJual: 0,
    stokAkhirGr: 0,
    plusMinusGr: 0,
    rpStokAwal: 0,
    rpBeli: 0,
    rpJual: 0,
    rpStokAkhir: 0,
    biayaOP: 0,
    biayaPusat: 0,
    labaRugi: 0
  });

  // Add central expenses to grand total (PUSAT row is now always displayed)
  grandTotals.biayaOP += centralExpensesTotal;
  grandTotals.labaRugi += pusatLabaRugi;

  const totalBeratBeli = grandTotals.beratBeli;
  const totalBeratJual = grandTotals.beratJual;
  const totalStokAwalGr = grandTotals.stokAwalGr;
  const totalStokAkhirGr = grandTotals.stokAkhirGr;
  const grandPlusMinusPersen = (totalBeratBeli + totalStokAwalGr) > 0 ? (grandTotals.plusMinusGr / (totalBeratBeli + totalStokAwalGr)) * 100 : 0;
  const grandBiayaOPPerGr = totalBeratJual > 0 ? (grandTotals.biayaOP + grandTotals.biayaPusat) / totalBeratJual : 0;
  // Grand LabaRugi/gr = Total LabaRugi / Total Berat Jual
  const grandLabaRugiPerGr = totalBeratJual > 0 ? grandTotals.labaRugi / totalBeratJual : 0;

  const fmtDate = (d) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleDateString('id-ID'); } catch { return '-'; }
  };

  // TAB B: Rekapan Sebagian — simplified columns
  const copySummaryRow = async () => {
    const values = [
      Math.round(sebagianSummary.modal),
      Math.round(sebagianSummary.beratPenjualan),
      Math.round(grandTotals.labaRugi),
      Math.round(sebagianSummary.biayaPusat),
      Math.round(sebagianSummary.rpJual),
    ];
    const text = values.join('\t');
    // Excel kadang memperlakukan teks tab sebagai satu kolom (tergantung setelan regional),
    // jadi sertakan juga format HTML <table> agar selalu di-paste sebagai 1 baris 5 kolom.
    const html = `<table><tr>${values.map((v) => `<td>${v}</td>`).join('')}</tr></table>`;
    try {
      if (navigator.clipboard && window.ClipboardItem && window.isSecureContext) {
        await navigator.clipboard.write([
          new window.ClipboardItem({
            'text/plain': new Blob([text], { type: 'text/plain' }),
            'text/html': new Blob([html], { type: 'text/html' }),
          }),
        ]);
      } else if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      toast.success('Data tersalin — siap di-paste ke Excel');
    } catch (error) {
      toast.error('Gagal menyalin data');
    }
  };

  const renderRekapanSebagian = () => {
    const rows = rekapData.slice().sort((a, b) => a.database_index - b.database_index);
    const visible = getVisibleBranches(rows);
    const totals = visible.reduce((t, b) => ({
      beratJual: t.beratJual + b.beratJual,
      biayaOP: t.biayaOP + b.biayaOP,
      biayaPusat: t.biayaPusat + b.biayaPusat,
      plusMinusGr: t.plusMinusGr + b.plusMinusGr,
      labaRugi: t.labaRugi + b.labaRugi,
      beratBeliBase: t.beratBeliBase + (b.beratBeli + b.stokAwalGr),
    }), { beratJual: 0, biayaOP: 0, biayaPusat: 0, plusMinusGr: 0, labaRugi: 0, beratBeliBase: 0 });
    const totalBiayaGabunganPerGr = totals.beratJual > 0 ? (totals.biayaOP + totals.biayaPusat) / totals.beratJual : 0;
    const totalLabaRugiPerGr = totals.beratJual > 0 ? totals.labaRugi / totals.beratJual : 0;
    const totalPlusMinusPersen = totals.beratBeliBase > 0 ? (totals.plusMinusGr / totals.beratBeliBase) * 100 : 0;
    return (
      <Card ref={sebagianRef}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-xl">
              <Building2 size={24} className="text-teal-600" />
              Rekapan Sebagian{periodName ? ` - ${periodName}` : ''}
            </CardTitle>
            {!capturing && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => captureRef(sebagianRef, `Rekapan-Sebagian${periodName ? '-' + periodName : ''}`)}
                className="gap-2"
                data-testid="sebagian-screenshot-btn"
              >
                <Camera size={16} /> Screenshot
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table className="text-xs [&_th]:whitespace-nowrap [&_th]:px-2 [&_th]:py-2 [&_td]:whitespace-nowrap [&_td]:px-2 [&_td]:py-1.5">
              <TableHeader>
                <TableRow className="bg-slate-100">
                  <TableHead className="text-xs">No.</TableHead>
                  <TableHead className="text-xs">Nama</TableHead>
                  <TableHead className="text-xs text-right">Berat Jual (gr)</TableHead>
                  <TableHead className="text-xs text-right">Biaya OP / gr</TableHead>
                  <TableHead className="text-xs text-right">LabaRugi / gr</TableHead>
                  <TableHead className="text-xs text-right">+/- gr (%)</TableHead>
                  <TableHead className="text-xs text-right">+/- gr</TableHead>
                  <TableHead className="text-xs text-right">LabaRugi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visible.map((branch, index) => {
                  const biayaGabunganPerGr = branch.beratJual > 0 ? (branch.biayaOP + branch.biayaPusat) / branch.beratJual : 0;
                  return (
                    <TableRow
                      key={branch.branch_id}
                      className={`${index % 2 === 0 ? 'bg-slate-50' : 'bg-white'} cursor-pointer hover:bg-teal-50 glow-row`}
                      onClick={() => handleBranchClick(branch.branch_id)}
                      data-testid={`sebagian-row-${branch.branch_id}`}
                    >
                      <TableCell className="text-center">{index + 1}</TableCell>
                      <TableCell className="font-medium">{branch.branch_name}</TableCell>
                      <TableCell className="text-right">{formatNumber(branch.beratJual)}</TableCell>
                      <TableCell className={`text-right font-semibold ${biayaGabunganPerGr > 400 ? 'bg-red-100 text-red-700' : ''}`}>
                        {formatNumber(biayaGabunganPerGr)}
                      </TableCell>
                      <TableCell className={`text-right font-semibold ${getLabaRugiColor(branch.labaRugiPerGr)}`}>
                        {formatNumber(branch.labaRugiPerGr)}
                      </TableCell>
                      <TableCell className={`text-right ${getPercentageColor(branch.plusMinusPersen)}`}>
                        {branch.plusMinusPersen.toFixed(2)}%
                      </TableCell>
                      <TableCell className="text-right">{formatNumber(branch.plusMinusGr)}</TableCell>
                      <TableCell className="text-right font-bold">{formatCurrency(branch.labaRugi)}</TableCell>
                    </TableRow>
                  );
                })}
                {visible.length === 0 && (
                  <TableRow><TableCell colSpan={8} className="text-center text-slate-400 italic py-4">Belum ada data</TableCell></TableRow>
                )}
              </TableBody>
              <tfoot>
                <TableRow className="bg-blue-100 border-t-2 border-blue-600">
                  <TableCell colSpan={2} className="font-bold text-blue-800">TOTAL</TableCell>
                  <TableCell className="text-right font-bold">{formatNumber(totals.beratJual)}</TableCell>
                  <TableCell className={`text-right font-bold ${totalBiayaGabunganPerGr > 400 ? 'bg-red-100 text-red-700' : ''}`}>{formatNumber(totalBiayaGabunganPerGr)}</TableCell>
                  <TableCell className="text-right font-bold">{formatNumber(totalLabaRugiPerGr)}</TableCell>
                  <TableCell className={`text-right font-bold ${getPercentageColor(totalPlusMinusPersen)}`}>{totalPlusMinusPersen.toFixed(2)}%</TableCell>
                  <TableCell className="text-right font-bold">{formatNumber(totals.plusMinusGr)}</TableCell>
                  <TableCell className="text-right font-bold">{formatCurrency(totals.labaRugi)}</TableCell>
                </TableRow>
              </tfoot>
            </Table>
          </div>
          
          {/* Ringkasan periode: Modal / Berat Penjualan / Laba Rugi / Biaya Pusat / Rp Jual */}
          <div className="mt-8 overflow-x-auto" data-testid="sebagian-summary-table">
            <div className="flex items-center justify-end mb-2">
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                data-testid="sebagian-summary-copy-btn"
                onClick={copySummaryRow}
              >
                <Copy className="h-4 w-4" />
                Copy
              </Button>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-100 border-y border-slate-300">
                  <TableHead className="text-xs text-right whitespace-nowrap font-semibold">Modal</TableHead>
                  <TableHead className="text-xs text-right whitespace-nowrap font-semibold">Berat Penjualan (gr)</TableHead>
                  <TableHead className="text-xs text-right whitespace-nowrap font-semibold">Laba Rugi</TableHead>
                  <TableHead className="text-xs text-right whitespace-nowrap font-semibold">Biaya Pusat</TableHead>
                  <TableHead className="text-xs text-right whitespace-nowrap font-semibold">Rp Jual</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow className="bg-amber-50">
                  <TableCell className="text-right font-bold" data-testid="sebagian-summary-modal">{formatCurrency(sebagianSummary.modal)}</TableCell>
                  <TableCell className="text-right font-bold" data-testid="sebagian-summary-berat">{formatNumber(sebagianSummary.beratPenjualan)}</TableCell>
                  <TableCell className="text-right font-bold" data-testid="sebagian-summary-labarugi">{formatCurrency(grandTotals.labaRugi)}</TableCell>
                  <TableCell className="text-right font-bold" data-testid="sebagian-summary-biaya-pusat">{formatCurrency(sebagianSummary.biayaPusat)}</TableCell>
                  <TableCell className="text-right font-bold" data-testid="sebagian-summary-rpjual">{formatCurrency(sebagianSummary.rpJual)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    );
  };

  // TAB D: Seluruh Biaya — central expenses (exclude income) + all branch expenses
  const renderSeluruhBiaya = () => {
    const branchNameById = {};
    branchesList.forEach(b => { branchNameById[b.id] = b.name; });
    // Central expenses: exclude "Pendapatan Biaya Operasional" (operational-cost income entries)
    const pusatBiaya = allCentralExpenses.filter(e => {
      const nm = `${e.account_name || ''} ${e.description || ''}`.toLowerCase();
      return !nm.includes('pendapatan biaya operasional');
    });
    const totalPusat = pusatBiaya.reduce((s, e) => s + (e.amount || 0), 0);
    // Group central expenses by Jenis (expense_type)
    const pusatByJenis = {};
    pusatBiaya.forEach(e => {
      const j = e.expense_type || 'LAIN_LAIN';
      (pusatByJenis[j] = pusatByJenis[j] || []).push(e);
    });
    const jenisKeys = Object.keys(pusatByJenis);

    // Group branch expenses by branch (one table per branch)
    const cabangByBranch = {};
    allExpenses.forEach(e => {
      (cabangByBranch[e.branch_id] = cabangByBranch[e.branch_id] || []).push(e);
    });
    const branchOrder = branchesList.filter(b => cabangByBranch[b.id] && cabangByBranch[b.id].length > 0);

    return (
      <div ref={biayaRef} className="space-y-6 bg-white p-1">
        <div className="flex items-center justify-end">
          {!capturing && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => captureRef(biayaRef, `Seluruh-Biaya${periodName ? '-' + periodName : ''}`)}
              className="gap-2"
              data-testid="biaya-screenshot-btn"
            >
              <Camera size={16} /> Screenshot
            </Button>
          )}
        </div>

        {/* BIAYA PUSAT - grouped by Jenis in one table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg text-red-800">Biaya Pusat{periodName ? ` - ${periodName}` : ''}</CardTitle>
            <p className="text-sm text-slate-500">Seluruh biaya pusat (tanpa pendapatan biaya operasional), dipisah per jenis</p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table className="text-xs">
                <TableHeader>
                  <TableRow className="bg-slate-100">
                    <TableHead className="text-xs">Tanggal</TableHead>
                    <TableHead className="text-xs">Keterangan</TableHead>
                    <TableHead className="text-xs text-right">Jumlah</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {jenisKeys.map((jenis) => {
                    const rows = pusatByJenis[jenis];
                    const subtotal = rows.reduce((s, e) => s + (e.amount || 0), 0);
                    return (
                      <React.Fragment key={jenis}>
                        <TableRow className="bg-red-50">
                          <TableCell colSpan={3} className="font-bold text-red-800">{jenis}</TableCell>
                        </TableRow>
                        {rows.map((e, i) => (
                          <TableRow key={e.id || i} className="bg-white" data-testid={`pusat-biaya-row`}>
                            <TableCell>{fmtDate(e.expense_date || e.created_at)}</TableCell>
                            <TableCell>{e.description || '-'}</TableCell>
                            <TableCell className={`text-right font-semibold ${(e.amount || 0) < 0 ? 'text-green-600' : ''}`}>{formatCurrency(e.amount)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-slate-50 border-b">
                          <TableCell colSpan={2} className="text-right font-semibold text-slate-600">Subtotal {jenis}</TableCell>
                          <TableCell className="text-right font-bold">{formatCurrency(subtotal)}</TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                  {pusatBiaya.length === 0 && (
                    <TableRow><TableCell colSpan={3} className="text-center text-slate-400 italic py-4">Belum ada biaya pusat</TableCell></TableRow>
                  )}
                </TableBody>
                <tfoot>
                  <TableRow className="bg-red-100 border-t-2 border-red-400">
                    <TableCell colSpan={2} className="font-bold text-red-800">TOTAL BIAYA PUSAT</TableCell>
                    <TableCell className="text-right font-bold text-red-800">{formatCurrency(totalPusat)}</TableCell>
                  </TableRow>
                </tfoot>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* BIAYA CABANG - one table per branch */}
        {branchOrder.length === 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg text-orange-800">Biaya Cabang{periodName ? ` - ${periodName}` : ''}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-center text-slate-400 italic py-4">Belum ada biaya cabang</p>
            </CardContent>
          </Card>
        )}
        {branchOrder.map((branch) => {
          const rows = cabangByBranch[branch.id];
          const subtotal = rows.reduce((s, e) => s + (e.amount || 0), 0);
          // Group this branch's expenses by Partai
          const byPartai = {};
          rows.forEach(e => {
            const p = e.partai || '(Tanpa Partai)';
            (byPartai[p] = byPartai[p] || []).push(e);
          });
          const partaiKeys = Object.keys(byPartai);
          return (
            <Card key={branch.id} data-testid={`cabang-biaya-card-${branch.id}`}>
              <CardHeader className="py-3">
                <CardTitle className="text-base text-orange-800">Biaya Cabang {branch.name}{periodName ? ` - ${periodName}` : ''}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table className="text-xs">
                    <TableHeader>
                      <TableRow className="bg-slate-100">
                        <TableHead className="text-xs">Tanggal</TableHead>
                        <TableHead className="text-xs">Keterangan</TableHead>
                        <TableHead className="text-xs text-right">Jumlah</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {partaiKeys.map((partai) => {
                        const pRows = byPartai[partai];
                        const pSubtotal = pRows.reduce((s, e) => s + (e.amount || 0), 0);
                        return (
                          <React.Fragment key={partai}>
                            <TableRow className="bg-orange-50">
                              <TableCell colSpan={3} className="font-bold text-orange-800">Partai {partai}</TableCell>
                            </TableRow>
                            {pRows.map((e, i) => (
                              <TableRow key={e.id || i} className="bg-white">
                                <TableCell>{fmtDate(e.expense_date || e.created_at)}</TableCell>
                                <TableCell>{e.description || '-'}</TableCell>
                                <TableCell className="text-right font-semibold">{formatCurrency(e.amount)}</TableCell>
                              </TableRow>
                            ))}
                            <TableRow className="bg-slate-50 border-b">
                              <TableCell colSpan={2} className="text-right font-semibold text-slate-600">Subtotal {partai}</TableCell>
                              <TableCell className="text-right font-bold">{formatCurrency(pSubtotal)}</TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                    <tfoot>
                      <TableRow className="bg-orange-100 border-t-2 border-orange-400">
                        <TableCell colSpan={2} className="font-bold text-orange-800">TOTAL {branch.name}</TableCell>
                        <TableCell className="text-right font-bold text-orange-800">{formatCurrency(subtotal)}</TableCell>
                      </TableRow>
                    </tfoot>
                  </Table>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    );
  };

  // Pendapatan Biaya Operasional Pusat: bandingkan hasil tercatat vs berat jual x biaya/gr
  const expectedOpIncome = rekapData.reduce((s, r) => s + (r.biayaPusat || 0), 0);
  const actualOpIncome = allCentralExpenses
    .filter((e) => (e.description || '').startsWith('Pendapatan dari'))
    .reduce((s, e) => s - (e.amount || 0), 0);
  const opIncomeMismatch = Math.round(expectedOpIncome) !== Math.round(actualOpIncome);

  const handleRecalcOperational = async () => {
    if (!activePeriodId) {
      toast.error('Periode aktif tidak ditemukan');
      return;
    }
    setRecalcingOp(true);
    try {
      const res = await api.post(`/periods/${activePeriodId}/recalculate-operational`);
      await fetchRekapData();
      toast.success(`Rekalkulasi selesai: ${formatCurrency(res.data.total_income || 0)}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Rekalkulasi gagal');
    } finally {
      setRecalcingOp(false);
    }
  };

  // TAB E: Stok — riwayat stok per tanggal dengan saldo berjalan
  const canEditStok = canManageFinance(user);
  const stokGudangPartai = branchesList.reduce((s, b) => s + (stokGudangByBranch[b.id] || 0), 0);

  const stokRows = (() => {
    let lapangan = 0;
    let gudang = 0;
    return stockRecords.map((r) => {
      lapangan = lapangan + (r.pembelian_baru || 0) - (r.bahan_dikirim || 0);
      gudang = gudang + (r.bahan_dikirim || 0) - (r.bahan_terjual || 0);
      return { ...r, stok_lapangan: lapangan, stok_gudang: gudang, total: lapangan + gudang };
    });
  })();
  const lastRow = stokRows[stokRows.length - 1];
  const stokGudangTabel = lastRow ? lastRow.stok_gudang : 0;
  const stokAkurat = Math.round(stokGudangTabel) === Math.round(stokGudangPartai);

  const formatTanggal = (d) => {
    if (!d) return '-';
    const [y, m, day] = d.split('-');
    return `${day}/${m}/${y.slice(2)}`;
  };

  const formatRibuan = (val) => {
    const digits = String(val).replace(/\D/g, '');
    return digits ? Number(digits).toLocaleString('id-ID') : '';
  };

  const openStokDialog = (record = null) => {
    if (record) {
      setEditingStokId(record.id);
      setStokForm({
        date: record.date || '',
        pembelian_baru: formatRibuan(record.pembelian_baru || 0),
        bahan_dikirim: formatRibuan(record.bahan_dikirim || 0),
        bahan_terjual: formatRibuan(record.bahan_terjual || 0),
      });
    } else {
      setEditingStokId(null);
      setStokForm({
        date: new Date().toISOString().slice(0, 10),
        pembelian_baru: '',
        bahan_dikirim: '',
        bahan_terjual: '',
      });
    }
    setStokDialogOpen(true);
  };

  const handleSaveStokRecord = async () => {
    if (!stokForm.date) {
      toast.error('Tanggal wajib diisi');
      return;
    }
    setSavingStok(true);
    try {
      const periodId = localStorage.getItem('selectedPeriodId');
      const payload = {
        period_id: periodId || null,
        date: stokForm.date,
        pembelian_baru: parseFloat(String(stokForm.pembelian_baru).replace(/\./g, '')) || 0,
        bahan_dikirim: parseFloat(String(stokForm.bahan_dikirim).replace(/\./g, '')) || 0,
        bahan_terjual: parseFloat(String(stokForm.bahan_terjual).replace(/\./g, '')) || 0,
      };
      if (editingStokId) {
        await api.put(`/stock/records/${editingStokId}`, payload);
      } else {
        await api.post('/stock/records', payload);
      }
      const res = await api.get('/stock/records', { params: periodId ? { period_id: periodId } : {} });
      setStockRecords(res.data || []);
      setStokDialogOpen(false);
      toast.success('Data stok berhasil disimpan');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan data stok');
    } finally {
      setSavingStok(false);
    }
  };

  const handleDeleteStokRecord = async (record) => {
    if (!window.confirm(`Hapus data stok tanggal ${formatTanggal(record.date)}?`)) return;
    try {
      await api.delete(`/stock/records/${record.id}`);
      setStockRecords(stockRecords.filter((r) => r.id !== record.id));
      toast.success('Data stok dihapus');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghapus data stok');
    }
  };

  const renderRekapanPenjualan = () => {
    const visibleRows = hideNoSalesBranches
      ? penjualanRekap.filter((r) => r.hasSales)
      : penjualanRekap;
    const groups = [
      { status: 'ACTIVE', title: 'Rekapan Penjualan', accent: 'text-emerald-600', head: 'bg-slate-100', testId: 'rekapan-penjualan-card' },
      { status: 'INACTIVE', title: 'Cabang Non Aktif', accent: 'text-slate-500', head: 'bg-slate-100', testId: 'rekapan-penjualan-nonaktif-card' },
      { status: 'BLACKLIST', title: 'Cabang Blacklist', accent: 'text-red-500', head: 'bg-red-50', testId: 'rekapan-penjualan-blacklist-card' },
    ];

    const renderTable = (group) => {
      const rows = visibleRows.filter((r) => r.status === group.status);
      if (group.status !== 'ACTIVE' && rows.length === 0) return null;
      const totals = rows.reduce(
        (acc, row) => ({ berat: acc.berat + row.berat, laba: acc.laba + row.laba }),
        { berat: 0, laba: 0 }
      );
      const totalMargin = totals.berat !== 0 ? totals.laba / totals.berat : 0;

      return (
        <Card key={group.status} data-testid={group.testId} ref={group.status === 'ACTIVE' ? penjualanRef : undefined}>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-xl">
                  <Building2 size={24} className={group.accent} />
                  {group.title}{group.status === 'ACTIVE' && periodName ? ` - ${periodName}` : ''}
                </CardTitle>
                <p className="text-sm text-slate-500">
                  {group.status === 'ACTIVE'
                    ? 'Laba hanya dari partai yang sudah memiliki penjualan'
                    : `Cabang berstatus ${group.status === 'INACTIVE' ? 'Non Aktif' : 'Blacklist'}`}
                </p>
              </div>
              {group.status === 'ACTIVE' && !capturing && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setHideNoSalesBranches((v) => !v)}
                    className="gap-2"
                    data-testid="penjualan-toggle-nosales-btn"
                  >
                    {hideNoSalesBranches ? <><Eye size={16} /> Tampilkan Tanpa Penjualan</> : <><EyeOff size={16} /> Sembunyikan Tanpa Penjualan</>}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => captureRef(penjualanRef, `Rekapan-Penjualan${periodName ? '-' + periodName : ''}`)}
                    className="gap-2"
                    data-testid="penjualan-screenshot-btn"
                  >
                    <Camera size={16} /> Screenshot
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <Table className="text-xs [&_th]:whitespace-nowrap [&_td]:whitespace-nowrap">
              <TableHeader>
                <TableRow className={group.head}>
                  <TableHead className="w-12">No.</TableHead>
                  <TableHead>Cabang</TableHead>
                  <TableHead className="text-right">Berat (gr)</TableHead>
                  <TableHead className="text-right">Laba</TableHead>
                  <TableHead className="text-right">Margin</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-slate-500 py-6" data-testid="rekapan-penjualan-empty">
                      Belum ada penjualan pada periode ini
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row, index) => (
                    <TableRow
                      key={row.branch_id}
                      className={!row.hasSales ? 'bg-slate-50/60' : ''}
                      data-testid={`rekapan-penjualan-row-${row.branch_id}`}
                    >
                      <TableCell>{index + 1}</TableCell>
                      <TableCell
                        className="font-medium text-blue-700 cursor-pointer hover:underline"
                        onClick={() => handleBranchClick(row.branch_id)}
                        data-testid={`rekapan-penjualan-branch-${row.branch_id}`}
                      >
                        {row.branch_name}
                      </TableCell>
                      <TableCell className="text-right" data-testid={`rekapan-penjualan-berat-${row.branch_id}`}>
                        {formatNumber(row.berat)}
                      </TableCell>
                      <TableCell
                        className={`text-right font-semibold ${row.laba < 0 ? 'text-red-600' : 'text-slate-800'}`}
                        data-testid={`rekapan-penjualan-laba-${row.branch_id}`}
                      >
                        {formatCurrency(row.laba)}
                      </TableCell>
                      <TableCell
                        className={`text-right font-semibold ${getLabaRugiColor(row.margin)}`}
                        data-testid={`rekapan-penjualan-margin-${row.branch_id}`}
                      >
                        {formatNumber(row.margin)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
              {rows.length > 0 && (
                <tfoot>
                  <TableRow className="bg-slate-50 font-bold" data-testid={`rekapan-penjualan-total-row-${group.status.toLowerCase()}`}>
                    <TableCell colSpan={2}>TOTAL</TableCell>
                    <TableCell className="text-right" data-testid={`rekapan-penjualan-total-berat-${group.status.toLowerCase()}`}>{formatNumber(totals.berat)}</TableCell>
                    <TableCell className={`text-right ${totals.laba < 0 ? 'text-red-600' : ''}`} data-testid={`rekapan-penjualan-total-laba-${group.status.toLowerCase()}`}>
                      {formatCurrency(totals.laba)}
                    </TableCell>
                    <TableCell className="text-right" data-testid={`rekapan-penjualan-total-margin-${group.status.toLowerCase()}`}>{formatNumber(totalMargin)}</TableCell>
                  </TableRow>
                </tfoot>
              )}
            </Table>
          </CardContent>
        </Card>
      );
    };

    return (
      <div className="space-y-4">
        {groups.map(renderTable)}
      </div>
    );
  };

  const renderStok = () => (
    <div className="space-y-3">
      {canEditStok && (
        <div className="flex justify-start">
          <Button variant="outline" size="sm" className="gap-2" onClick={() => openStokDialog()} data-testid="stok-add-btn">
            <Plus size={16} /> Tambah Data
          </Button>
        </div>
      )}
      <div className="flex flex-col lg:flex-row gap-4 items-start">
        <Card data-testid="stok-card" className="w-full lg:flex-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <Package size={24} className="text-teal-600" />
              Stok{periodName ? ` - ${periodName}` : ''}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="[&>div]:overflow-visible">
              <Table className="text-[11px] [&_th]:whitespace-nowrap [&_th]:px-1.5 [&_th]:py-1 [&_td]:whitespace-nowrap [&_td]:px-1.5 [&_td]:py-0.5">
                <TableHeader>
                  <TableRow className="bg-slate-100 [&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:bg-slate-100 [&_th]:h-auto">
                    <TableHead className="text-[11px]">Tanggal</TableHead>
                    <TableHead className="text-[11px] text-right">Pembelian Baru (gr)</TableHead>
                    <TableHead className="text-[11px] text-right">Bahan Dikirim (gr)</TableHead>
                    <TableHead className="text-[11px] text-right">Stok Lapangan (gr)</TableHead>
                    <TableHead className="text-[11px] text-right">Bahan Terjual (gr)</TableHead>
                    <TableHead className="text-[11px] text-right">Stok Gudang + Dijalan (gr)</TableHead>
                    <TableHead className="text-[11px] text-right">Total Stok (gr)</TableHead>
                    {canEditStok && <TableHead className="text-[11px] text-center">Aksi</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stokRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={canEditStok ? 8 : 7} className="text-center text-slate-500 py-6">
                        Belum ada data stok
                      </TableCell>
                    </TableRow>
                  ) : stokRows.map((r, idx) => (
                    <TableRow key={r.id} className="bg-white glow-row" data-testid={`stok-row-${r.id}`}>
                      <TableCell className="font-medium text-slate-700">{formatTanggal(r.date)}</TableCell>
                      <TableCell className="text-right text-green-600 font-medium">{formatNumber(r.pembelian_baru || 0)}</TableCell>
                      <TableCell className="text-right text-red-600 font-medium">{formatNumber(r.bahan_dikirim || 0)}</TableCell>
                      <TableCell className="text-right font-semibold">{formatNumber(r.stok_lapangan)}</TableCell>
                      <TableCell className="text-right text-red-600 font-medium">{formatNumber(r.bahan_terjual || 0)}</TableCell>
                      <TableCell className="text-right font-semibold">{formatNumber(r.stok_gudang)}</TableCell>
                      <TableCell className="text-right font-bold">{formatNumber(r.total)}</TableCell>
                      {canEditStok && (
                        <TableCell className="text-center">
                          {idx === stokRows.length - 1 && (
                            <div className="flex items-center justify-center gap-0.5">
                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => openStokDialog(r)} data-testid={`stok-edit-${r.id}`}>
                                <Pencil size={13} />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-6 w-6 text-red-600" onClick={() => handleDeleteStokRecord(r)} data-testid={`stok-delete-${r.id}`}>
                                <Trash2 size={13} />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <Card className={`w-full lg:w-72 ${stokAkurat ? '' : 'border-red-400'}`} data-testid="stok-partai-card">
          <CardHeader>
            <CardTitle className="text-base">Stok Gudang + Dijalan (Partai)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className={`text-2xl font-bold ${stokAkurat ? 'text-slate-800' : 'text-red-600'}`} data-testid="stok-partai-value">
              {formatNumber(stokGudangPartai)} <span className="text-sm font-normal">gr</span>
            </p>
            {!stokAkurat && (
              <p className="text-sm font-semibold text-red-600" data-testid="stok-tidak-akurat">
                Stok Tidak Akurat
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );


  return (
    <div className="space-y-6">
      <Tabs defaultValue="penuh" className="w-full">
        <TabsList className={pillTabsList}>
          <TabsTrigger value="penuh" className={pillTabsTrigger} data-testid="rekap-tab-penuh">Rekapan Penuh</TabsTrigger>
          <TabsTrigger value="sebagian" className={pillTabsTrigger} data-testid="rekap-tab-sebagian">Rekapan Sebagian</TabsTrigger>
          <TabsTrigger value="partai" className={pillTabsTrigger} data-testid="rekap-tab-partai">Rekapan Partai</TabsTrigger>
          <TabsTrigger value="biaya" className={pillTabsTrigger} data-testid="rekap-tab-biaya">Seluruh Biaya</TabsTrigger>
          <TabsTrigger value="penjualan" className={pillTabsTrigger} data-testid="rekap-tab-penjualan">Rekapan Penjualan</TabsTrigger>
          <TabsTrigger value="stok" className={pillTabsTrigger} data-testid="rekap-tab-stok">Stok</TabsTrigger>
        </TabsList>

        <TabsContent value="penuh" className="mt-4 space-y-6">
      <Card ref={cardRef}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-2xl">
                <Building2 size={28} className="text-teal-600" />
                Rekapan Semua Cabang{periodName ? ` - ${periodName}` : ''}
              </CardTitle>
              <p className="text-sm text-slate-600 mt-2">
                Total keseluruhan per cabang untuk periode yang dipilih
              </p>
            </div>
            {!capturing && (
              <div className="flex items-center gap-2">
                {opIncomeMismatch && canManageFinance(user) && (
                  <Button
                    onClick={handleRecalcOperational}
                    disabled={recalcingOp}
                    size="sm"
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid="recalc-op-income-btn"
                  >
                    <RefreshCw size={16} className={`mr-2 ${recalcingOp ? 'animate-spin' : ''}`} />
                    {recalcingOp ? 'Rekalkulasi...' : 'Biaya OP Pusat Error, Rekalkulasi Ulang'}
                  </Button>
                )}
                {zeroBranchesCount > 0 && (
                  <Button
                    onClick={() => setHideZeroBranches(!hideZeroBranches)}
                    variant={hideZeroBranches ? "default" : "outline"}
                    size="sm"
                    className={hideZeroBranches ? "bg-teal-600 hover:bg-teal-700" : ""}
                    title={hideZeroBranches ? "Tampilkan Semua Cabang" : "Sembunyikan Cabang Kosong"}
                  >
                    {hideZeroBranches ? <Eye size={16} className="mr-2" /> : <EyeOff size={16} className="mr-2" />}
                    {hideZeroBranches ? `Tampilkan (${zeroBranchesCount})` : `Sembunyikan Kosong (${zeroBranchesCount})`}
                  </Button>
                )}
                <Button
                  onClick={handleSaveToClipboard}
                  disabled={loading || capturing}
                  variant="outline"
                  size="icon"
                  title="Simpan sebagai Gambar"
                  data-testid="rekap-penuh-capture-btn"
                >
                  <Camera size={18} />
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table className="text-xs [&_th]:whitespace-nowrap [&_th]:px-2 [&_th]:py-2 [&_td]:whitespace-nowrap [&_td]:px-2 [&_td]:py-1.5">
              {/* Only show main table header if there are NO groups */}
              {!hasAnyGroup && (
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs whitespace-nowrap">No.</TableHead>
                    <TableHead className="text-xs whitespace-nowrap">Nama Cabang</TableHead>
                    <TableHead className="text-xs text-right whitespace-nowrap">Stok Awal (gr)</TableHead>
                    <TableHead className="text-xs text-right whitespace-nowrap">Berat Beli (gr)</TableHead>
                    <TableHead className="text-xs text-right whitespace-nowrap">Berat Jual (gr)</TableHead>
                    <TableHead className="text-xs text-right whitespace-nowrap">Stok Akhir (gr)</TableHead>
                    <TableHead className="text-xs text-right whitespace-nowrap">+/- gr</TableHead>
                    <TableHead className="text-xs text-right whitespace-nowrap">+/- gr (%)</TableHead>
                    <TableHead className="text-xs text-right whitespace-nowrap">Rp Stok Awal</TableHead>
                    <TableHead className="text-xs text-right whitespace-nowrap">Rp Beli</TableHead>
                    <TableHead className="text-xs text-right whitespace-nowrap">Rp Jual</TableHead>
                    <TableHead className="text-xs text-right whitespace-nowrap">Rp Stok Akhir</TableHead>
                    <TableHead className="text-xs text-right whitespace-nowrap">Biaya OP + {defaultOperationalCost}</TableHead>
                    <TableHead className="text-xs text-right whitespace-nowrap">Biaya OP/gr</TableHead>
                    <TableHead className="text-xs text-right whitespace-nowrap">LabaRugi</TableHead>
                    <TableHead className="text-xs text-right whitespace-nowrap">LabaRugi/gr</TableHead>
                  </TableRow>
                </TableHeader>
              )}
              <TableBody>
                {/* PUSAT Row for Flat List - FIRST ROW when NO groups */}
                {!hasAnyGroup && mainRekapData.length > 0 && (
                  <TableRow 
                    className="bg-green-50 border-b border-green-200 cursor-pointer hover:bg-green-100 glow-row"
                    onClick={handlePusatClick}
                  >
                    <TableCell className="text-center text-green-900">-</TableCell>
                    <TableCell className="font-bold text-green-900 pl-8">PUSAT</TableCell>
                    <TableCell className="text-right text-green-900">0</TableCell>
                    <TableCell className="text-right text-green-900">0</TableCell>
                    <TableCell className="text-right text-green-900">0</TableCell>
                    <TableCell className="text-right text-green-900">0</TableCell>
                    <TableCell className="text-right text-green-900">0</TableCell>
                    <TableCell className="text-right text-green-900">0.00%</TableCell>
                    <TableCell className="text-right text-green-900">Rp 0</TableCell>
                    <TableCell className="text-right text-green-900">Rp 0</TableCell>
                    <TableCell className="text-right text-green-900">Rp 0</TableCell>
                    <TableCell className="text-right text-green-900">Rp 0</TableCell>
                    <TableCell className="text-right font-bold text-green-900">{formatCurrency(centralExpensesTotal)}</TableCell>
                    <TableCell className="text-right text-green-900">0</TableCell>
                    <TableCell className="text-right font-bold text-green-900">{formatCurrency(pusatLabaRugi)}</TableCell>
                    <TableCell className="text-right text-green-900">0</TableCell>
                  </TableRow>
                )}

                {/* Render branches - grouped if any has group, flat if none */}
                {!hasAnyGroup ? (
                  // Flat list (no grouping) - preserve database order, filter if hideZeroBranches
                  (() => {
                    const visibleBranches = getVisibleBranches(
                      [...mainRekapData].sort((a, b) => a.database_index - b.database_index)
                    );
                    return visibleBranches.map((branch, index) => (
                      <TableRow 
                        key={branch.branch_id} 
                        className={`${index % 2 === 0 ? 'bg-slate-50' : 'bg-white'} cursor-pointer hover:bg-teal-50 glow-row`}
                        onClick={() => handleBranchClick(branch.branch_id)}
                      >
                        <TableCell className="text-center">{index + 1}</TableCell>
                        <TableCell className="font-medium whitespace-nowrap">
                          {branch.branch_name}
                        </TableCell>
                        <TableCell className="text-right">{formatNumber(branch.stokAwalGr)}</TableCell>
                        <TableCell className="text-right">{formatNumber(branch.beratBeli)}</TableCell>
                        <TableCell className="text-right">{formatNumber(branch.beratJual)}</TableCell>
                        <TableCell className="text-right">{formatNumber(branch.stokAkhirGr)}</TableCell>
                        <TableCell className="text-right">{formatNumber(branch.plusMinusGr)}</TableCell>
                        <TableCell className={`text-right ${getPercentageColor(branch.plusMinusPersen)}`}>
                          {branch.plusMinusPersen.toFixed(2)}%
                        </TableCell>
                        <TableCell className="text-right">{formatCurrency(branch.rpStokAwal)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(branch.rpBeli)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(branch.rpJual)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(branch.rpStokAkhir)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(branch.biayaOP + branch.biayaPusat)}</TableCell>
                        <TableCell className={`text-right ${branch.biayaOPPerGr > 400 ? 'bg-red-100 text-red-700 font-semibold' : ''}`}>{formatNumber(branch.biayaOPPerGr)}</TableCell>
                        <TableCell className="text-right font-bold">{formatCurrency(branch.labaRugi)}</TableCell>
                        <TableCell className={`text-right font-bold ${getLabaRugiColor(branch.labaRugiPerGr)}`}>
                          {formatNumber(branch.labaRugiPerGr)}
                        </TableCell>
                      </TableRow>
                    ));
                  })()
                ) : (
                  // Grouped list (with group headers and subtotals)
                  sortedGroupNames.map((groupName, groupIndex) => (
                    <React.Fragment key={groupName}>
                      {/* Spacer Row - Add white space between groups (same height as header row) */}
                      {groupIndex > 0 && (
                        <TableRow className="bg-white border-y border-slate-300">
                          <TableHead className="text-xs whitespace-nowrap font-semibold text-transparent">No.</TableHead>
                          <TableHead className="text-xs whitespace-nowrap font-semibold text-transparent">Nama Cabang</TableHead>
                          <TableHead className="text-xs text-right whitespace-nowrap font-semibold text-transparent">Stok Awal (gr)</TableHead>
                          <TableHead className="text-xs text-right whitespace-nowrap font-semibold text-transparent">Berat Beli (gr)</TableHead>
                          <TableHead className="text-xs text-right whitespace-nowrap font-semibold text-transparent">Berat Jual (gr)</TableHead>
                          <TableHead className="text-xs text-right whitespace-nowrap font-semibold text-transparent">Stok (gr)</TableHead>
                          <TableHead className="text-xs text-right whitespace-nowrap font-semibold text-transparent">+/- gr</TableHead>
                          <TableHead className="text-xs text-right whitespace-nowrap font-semibold text-transparent">+/- gr (%)</TableHead>
                          <TableHead className="text-xs text-right whitespace-nowrap font-semibold text-transparent">Rp Stok Awal</TableHead>
                          <TableHead className="text-xs text-right whitespace-nowrap font-semibold text-transparent">Rp Beli</TableHead>
                          <TableHead className="text-xs text-right whitespace-nowrap font-semibold text-transparent">Rp Jual</TableHead>
                          <TableHead className="text-xs text-right whitespace-nowrap font-semibold text-transparent">Rp Stok</TableHead>
                          <TableHead className="text-xs text-right whitespace-nowrap font-semibold text-transparent">Biaya OP</TableHead>
                          <TableHead className="text-xs text-right whitespace-nowrap font-semibold text-transparent">Biaya OP/gr</TableHead>
                          <TableHead className="text-xs text-right whitespace-nowrap font-semibold text-transparent">LabaRugi</TableHead>
                          <TableHead className="text-xs text-right whitespace-nowrap font-semibold text-transparent">LabaRugi/gr</TableHead>
                        </TableRow>
                      )}

                      {/* Group Header Row */}
                      <TableRow className="bg-blue-50 border-y-2 border-blue-300">
                        <TableCell colSpan={16} className="font-bold text-blue-800 text-sm py-2">
                          📁 {groupName}{periodName ? ` - ${periodName}` : ''}
                        </TableCell>
                      </TableRow>

                      {/* Table Header for this group */}
                      <TableRow className="bg-slate-100 border-y border-slate-300">
                        <TableHead className="text-xs whitespace-nowrap font-semibold">No.</TableHead>
                        <TableHead className="text-xs whitespace-nowrap font-semibold">Nama Cabang</TableHead>
                        <TableHead className="text-xs text-right whitespace-nowrap font-semibold">Stok Awal (gr)</TableHead>
                        <TableHead className="text-xs text-right whitespace-nowrap font-semibold">Berat Beli (gr)</TableHead>
                        <TableHead className="text-xs text-right whitespace-nowrap font-semibold">Berat Jual (gr)</TableHead>
                        <TableHead className="text-xs text-right whitespace-nowrap font-semibold">Stok (gr)</TableHead>
                        <TableHead className="text-xs text-right whitespace-nowrap font-semibold">+/- gr</TableHead>
                        <TableHead className="text-xs text-right whitespace-nowrap font-semibold">+/- gr (%)</TableHead>
                        <TableHead className="text-xs text-right whitespace-nowrap font-semibold">Rp Stok Awal</TableHead>
                        <TableHead className="text-xs text-right whitespace-nowrap font-semibold">Rp Beli</TableHead>
                        <TableHead className="text-xs text-right whitespace-nowrap font-semibold">Rp Jual</TableHead>
                        <TableHead className="text-xs text-right whitespace-nowrap font-semibold">Rp Stok</TableHead>
                        <TableHead className="text-xs text-right whitespace-nowrap font-semibold">Biaya OP + {defaultOperationalCost}</TableHead>
                        <TableHead className="text-xs text-right whitespace-nowrap font-semibold">Biaya OP/gr</TableHead>
                        <TableHead className="text-xs text-right whitespace-nowrap font-semibold">LabaRugi</TableHead>
                        <TableHead className="text-xs text-right whitespace-nowrap font-semibold">LabaRugi/gr</TableHead>
                      </TableRow>

                      {/* PUSAT Row - only for Grup Pusat */}
                      {groupName === 'Grup Pusat' && (
                        <TableRow 
                          className="bg-green-50 border-b border-green-200 cursor-pointer hover:bg-green-100 glow-row"
                          onClick={handlePusatClick}
                        >
                          <TableCell className="text-center text-green-900">-</TableCell>
                          <TableCell className="font-bold text-green-900 pl-8">PUSAT</TableCell>
                          <TableCell className="text-right text-green-900">0</TableCell>
                          <TableCell className="text-right text-green-900">0</TableCell>
                          <TableCell className="text-right text-green-900">0</TableCell>
                          <TableCell className="text-right text-green-900">0</TableCell>
                          <TableCell className="text-right text-green-900">0</TableCell>
                          <TableCell className="text-right text-green-900">0.00%</TableCell>
                          <TableCell className="text-right text-green-900">Rp 0</TableCell>
                          <TableCell className="text-right text-green-900">Rp 0</TableCell>
                          <TableCell className="text-right text-green-900">Rp 0</TableCell>
                          <TableCell className="text-right text-green-900">Rp 0</TableCell>
                          <TableCell className="text-right font-bold text-green-900">{formatCurrency(centralExpensesTotal)}</TableCell>
                          <TableCell className="text-right text-green-900">0</TableCell>
                          <TableCell className="text-right font-bold text-green-900">{formatCurrency(pusatLabaRugi)}</TableCell>
                          <TableCell className="text-right text-green-900">0</TableCell>
                        </TableRow>
                      )}

                      {/* Branches in this group */}
                      {(() => {
                        const visibleBranches = getVisibleBranches(groupedBranches[groupName]);
                        return visibleBranches.map((branch, index) => {
                          // Calculate row number: start from 1, PUSAT row doesn't count
                          const rowNumber = index + 1;
                          return (
                          <TableRow 
                            key={branch.branch_id} 
                            className={`${index % 2 === 0 ? 'bg-slate-50' : 'bg-white'} cursor-pointer hover:bg-teal-50 glow-row`}
                            onClick={() => handleBranchClick(branch.branch_id)}
                          >
                            <TableCell className="text-center">{rowNumber}</TableCell>
                            <TableCell className="font-medium whitespace-nowrap pl-8">
                              {branch.branch_name}
                            </TableCell>
                          <TableCell className="text-right">{formatNumber(branch.stokAwalGr)}</TableCell>
                          <TableCell className="text-right">{formatNumber(branch.beratBeli)}</TableCell>
                          <TableCell className="text-right">{formatNumber(branch.beratJual)}</TableCell>
                          <TableCell className="text-right">{formatNumber(branch.stok)}</TableCell>
                          <TableCell className="text-right">{formatNumber(branch.plusMinusGr)}</TableCell>
                          <TableCell className={`text-right ${getPercentageColor(branch.plusMinusPersen)}`}>
                            {branch.plusMinusPersen.toFixed(2)}%
                          </TableCell>
                          <TableCell className="text-right">{formatCurrency(branch.rpStokAwal)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(branch.rpBeli)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(branch.rpJual)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(branch.rpStok)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(branch.biayaOP + branch.biayaPusat)}</TableCell>
                          <TableCell className={`text-right ${branch.biayaOPPerGr > 400 ? 'bg-red-100 text-red-700 font-semibold' : ''}`}>{formatNumber(branch.biayaOPPerGr)}</TableCell>
                          <TableCell className="text-right font-bold">{formatCurrency(branch.labaRugi)}</TableCell>
                          <TableCell className={`text-right font-bold ${getLabaRugiColor(branch.labaRugiPerGr)}`}>
                            {formatNumber(branch.labaRugiPerGr)}
                          </TableCell>
                        </TableRow>
                      );
                        });
                      })()}

                    {/* Group Subtotal Row */}
                    {(() => {
                      const groupTotal = groupTotals[groupName];
                      const groupBeratBeli = groupTotal.beratBeli;
                      const groupBeratJual = groupTotal.beratJual;
                      const groupStok = groupTotal.stokAkhirGr;
                      const groupPlusMinusPersen = groupBeratBeli > 0 ? (groupTotal.plusMinusGr / groupBeratBeli) * 100 : 0;
                      const groupBiayaOPPerGr = groupBeratJual > 0 ? (groupTotal.biayaOP + groupTotal.biayaPusat) / groupBeratJual : 0;
                      const groupBeratTerjual = groupBeratJual + groupStok;
                      const groupLabaRugiPerGr = groupBeratTerjual > 0 ? groupTotal.labaRugi / groupBeratTerjual : 0;

                      return (
                        <TableRow className="bg-yellow-100 border-b-2 border-yellow-400">
                          <TableCell colSpan={2} className="font-bold text-yellow-900">SUBTOTAL {groupName}</TableCell>
                          <TableCell className="text-right font-bold text-yellow-900">{formatNumber(groupTotal.stokAwalGr)}</TableCell>
                          <TableCell className="text-right font-bold text-yellow-900">{formatNumber(groupTotal.beratBeli)}</TableCell>
                          <TableCell className="text-right font-bold text-yellow-900">{formatNumber(groupTotal.beratJual)}</TableCell>
                          <TableCell className="text-right font-bold text-yellow-900">{formatNumber(groupTotal.stokAkhirGr)}</TableCell>
                          <TableCell className="text-right font-bold text-yellow-900">{formatNumber(groupTotal.plusMinusGr)}</TableCell>
                          <TableCell className={`text-right font-bold ${getPercentageColor(groupPlusMinusPersen)}`}>
                            {groupPlusMinusPersen.toFixed(2)}%
                          </TableCell>
                          <TableCell className="text-right font-bold text-yellow-900">{formatCurrency(groupTotal.rpStokAwal)}</TableCell>
                          <TableCell className="text-right font-bold text-yellow-900">{formatCurrency(groupTotal.rpBeli)}</TableCell>
                          <TableCell className="text-right font-bold text-yellow-900">{formatCurrency(groupTotal.rpJual)}</TableCell>
                          <TableCell className="text-right font-bold text-yellow-900">{formatCurrency(groupTotal.rpStokAkhir)}</TableCell>
                          <TableCell className="text-right font-bold text-yellow-900">{formatCurrency(groupTotal.biayaOP + groupTotal.biayaPusat)}</TableCell>
                          <TableCell className={`text-right font-bold ${groupBiayaOPPerGr > 400 ? 'bg-red-100 text-red-700' : 'text-yellow-900'}`}>{formatNumber(groupBiayaOPPerGr)}</TableCell>
                          <TableCell className="text-right font-bold text-yellow-900">{formatCurrency(groupTotal.labaRugi)}</TableCell>
                          <TableCell className={`text-right font-bold ${getLabaRugiColor(groupLabaRugiPerGr)}`}>
                            {formatNumber(groupLabaRugiPerGr)}
                          </TableCell>
                        </TableRow>
                      );
                    })()}
                  </React.Fragment>
                  ))
                )}
                
                {/* Spacer before Grand Total - same height as header row */}
                {mainRekapData.length > 0 && hasAnyGroup && (
                  <TableRow className="bg-white border-y border-slate-300">
                    <TableHead className="text-xs whitespace-nowrap font-semibold text-transparent">No.</TableHead>
                    <TableHead className="text-xs whitespace-nowrap font-semibold text-transparent">Nama Cabang</TableHead>
                    <TableHead className="text-xs text-right whitespace-nowrap font-semibold text-transparent">Stok Awal (gr)</TableHead>
                    <TableHead className="text-xs text-right whitespace-nowrap font-semibold text-transparent">Berat Beli (gr)</TableHead>
                    <TableHead className="text-xs text-right whitespace-nowrap font-semibold text-transparent">Berat Jual (gr)</TableHead>
                    <TableHead className="text-xs text-right whitespace-nowrap font-semibold text-transparent">Stok (gr)</TableHead>
                    <TableHead className="text-xs text-right whitespace-nowrap font-semibold text-transparent">+/- gr</TableHead>
                    <TableHead className="text-xs text-right whitespace-nowrap font-semibold text-transparent">+/- gr (%)</TableHead>
                    <TableHead className="text-xs text-right whitespace-nowrap font-semibold text-transparent">Rp Stok Awal</TableHead>
                    <TableHead className="text-xs text-right whitespace-nowrap font-semibold text-transparent">Rp Beli</TableHead>
                    <TableHead className="text-xs text-right whitespace-nowrap font-semibold text-transparent">Rp Jual</TableHead>
                    <TableHead className="text-xs text-right whitespace-nowrap font-semibold text-transparent">Rp Stok</TableHead>
                    <TableHead className="text-xs text-right whitespace-nowrap font-semibold text-transparent">Biaya OP</TableHead>
                    <TableHead className="text-xs text-right whitespace-nowrap font-semibold text-transparent">Biaya OP/gr</TableHead>
                    <TableHead className="text-xs text-right whitespace-nowrap font-semibold text-transparent">LabaRugi</TableHead>
                    <TableHead className="text-xs text-right whitespace-nowrap font-semibold text-transparent">LabaRugi/gr</TableHead>
                  </TableRow>
                )}
                
                {/* Grand Total Row */}
                {mainRekapData.length > 0 && (
                  <TableRow className="bg-blue-100 border-t-2 border-blue-600">
                    <TableCell colSpan={2} className="font-bold text-blue-800">TOTAL</TableCell>
                    <TableCell className="text-right font-bold">{formatNumber(grandTotals.stokAwalGr)}</TableCell>
                    <TableCell className="text-right font-bold">{formatNumber(grandTotals.beratBeli)}</TableCell>
                    <TableCell className="text-right font-bold">{formatNumber(grandTotals.beratJual)}</TableCell>
                    <TableCell className="text-right font-bold">{formatNumber(grandTotals.stokAkhirGr)}</TableCell>
                    <TableCell className="text-right font-bold">{formatNumber(grandTotals.plusMinusGr)}</TableCell>
                    <TableCell className={`text-right font-bold ${getPercentageColor(grandPlusMinusPersen)}`}>
                      {grandPlusMinusPersen.toFixed(2)}%
                    </TableCell>
                    <TableCell className="text-right font-bold">{formatCurrency(grandTotals.rpStokAwal)}</TableCell>
                    <TableCell className="text-right font-bold">{formatCurrency(grandTotals.rpBeli)}</TableCell>
                    <TableCell className="text-right font-bold">{formatCurrency(grandTotals.rpJual)}</TableCell>
                    <TableCell className="text-right font-bold">{formatCurrency(grandTotals.rpStokAkhir)}</TableCell>
                    <TableCell className="text-right font-bold">{formatCurrency(grandTotals.biayaOP + grandTotals.biayaPusat)}</TableCell>
                    <TableCell className={`text-right font-bold ${grandBiayaOPPerGr > 400 ? 'bg-red-100 text-red-700' : ''}`}>{formatNumber(grandBiayaOPPerGr)}</TableCell>
                    <TableCell className="text-right font-bold">{formatCurrency(grandTotals.labaRugi)}</TableCell>
                    <TableCell className={`text-right font-bold ${getLabaRugiColor(grandLabaRugiPerGr)}`}>
                      {formatNumber(grandLabaRugiPerGr)}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Tabel Cabang Non Aktif (terpisah) */}
      {nonAktifRekapData.length > 0 && renderStatusRekapTable({
        data: nonAktifRekapData,
        title: 'Cabang Non Aktif',
        subtitle: 'Cabang dengan status Non Aktif (tanpa transaksi pada periode ini)',
        isBlacklist: false,
        testId: 'nonaktif-rekapan-card',
        rowPrefix: 'nonaktif-branch-row',
      })}

      {/* Tabel Cabang Blacklist (terpisah) */}
      {blacklistRekapData.length > 0 && renderStatusRekapTable({
        data: blacklistRekapData,
        title: 'Cabang Blacklist',
        subtitle: 'Cabang dengan status Blacklist (tanpa transaksi pada periode ini)',
        isBlacklist: true,
        testId: 'blacklist-rekapan-card',
        rowPrefix: 'blacklist-branch-row',
      })}
        </TabsContent>

        <TabsContent value="sebagian" className="mt-4">
          {renderRekapanSebagian()}
        </TabsContent>

        <TabsContent value="partai" className="mt-4">
          <RekapanSemuaPartai onBranchSelect={onBranchSelect} />
        </TabsContent>

        <TabsContent value="biaya" className="mt-4">
          {renderSeluruhBiaya()}
        </TabsContent>

        <TabsContent value="penjualan" className="mt-4">
          {renderRekapanPenjualan()}
        </TabsContent>

        <TabsContent value="stok" className="mt-4">
          {renderStok()}
        </TabsContent>
      </Tabs>

      <Dialog open={stokDialogOpen} onOpenChange={setStokDialogOpen}>
        <DialogContent className="max-w-md" data-testid="stok-edit-dialog">
          <DialogHeader>
            <DialogTitle>{editingStokId ? 'Edit Data Stok' : 'Tambah Data Stok'}{periodName ? ` - ${periodName}` : ''}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <span className="text-sm text-slate-700">Tanggal</span>
              <Input
                type="date"
                value={stokForm.date}
                onChange={(e) => setStokForm({ ...stokForm, date: e.target.value })}
                data-testid="stok-date-input"
              />
            </div>
            {[
              { key: 'pembelian_baru', label: 'Pembelian Baru (gr)' },
              { key: 'bahan_dikirim', label: 'Bahan Dikirim (gr)' },
              { key: 'bahan_terjual', label: 'Bahan Terjual (gr)' },
            ].map((f) => (
              <div key={f.key} className="space-y-1">
                <span className="text-sm text-slate-700">{f.label}</span>
                <Input
                  type="text"
                  inputMode="numeric"
                  className="text-right"
                  value={stokForm[f.key]}
                  onChange={(e) => setStokForm({ ...stokForm, [f.key]: formatRibuan(e.target.value) })}
                  data-testid={`stok-${f.key.replace('_', '-')}-input`}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStokDialogOpen(false)} data-testid="stok-cancel-btn">Batal</Button>
            <Button onClick={handleSaveStokRecord} disabled={savingStok} className="bg-teal-600 hover:bg-teal-700" data-testid="stok-save-btn">
              {savingStok ? 'Menyimpan...' : 'Simpan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RekapanSemuaCabang;
